import { useCallback } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Table,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Separator,
} from '@chakra-ui/react'
import {
  X,
  ChevronUp,
  ChevronDown,
  MoreVertical,
  Edit,
  Trash2,
  Check,
  Copy,
  RotateCcw,
  Calendar,
  FileText,
  Clock,
  User,
} from 'lucide-react'
import { useTransaction } from '../hooks'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import type { TransactionStatus, TransactionLine, Transaction } from '../types'

interface TransactionDetailDrawerProps {
  transactionId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
  // Actions callbacks
  onEdit?: (transaction: Transaction) => void
  onDelete?: (id: string) => void
  onValidate?: (id: string) => void
  onDuplicate?: (transaction: Transaction) => void
  onReverse?: (id: string) => void
}

export function TransactionDetailDrawer({
  transactionId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
  onEdit,
  onDelete,
  onValidate,
  onDuplicate,
  onReverse,
}: TransactionDetailDrawerProps) {
  const { data: transaction, isLoading, error } = useTransaction(transactionId ?? undefined)

  const handleInteractOutside = useCallback((e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
    const target = e.detail.originalEvent.target as HTMLElement
    const tableRow = target.closest('[data-transaction-row]') as HTMLElement
    if (tableRow) return
    onClose()
  }, [onClose])

  const formatCurrency = (value: number | null | undefined) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0)
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = (status: TransactionStatus) => {
    const config: Record<TransactionStatus, { color: string; label: string }> = {
      draft: { color: 'gray', label: 'Brouillon' },
      validated: { color: 'green', label: 'Validé' },
      reversed: { color: 'red', label: 'Contrepassé' },
    }
    const { color, label } = config[status] || { color: 'gray', label: status }
    return (
      <Badge colorPalette={color} variant="subtle">
        {label}
      </Badge>
    )
  }

  const getAccountCode = (line: TransactionLine) => {
    // Utiliser custom_code de residence_chart_account s'il existe
    const customCode = (line as any).residence_chart_account?.custom_code
    if (customCode) return customCode

    // Sinon, utiliser le code du compte de base
    return line.account?.code || '???'
  }

  // Calcul des totaux
  const totals = transaction?.lines?.reduce(
    (acc, line) => ({
      debit: acc.debit + (line.debit || 0),
      credit: acc.credit + (line.credit || 0),
    }),
    { debit: 0, credit: 0 }
  ) || { debit: 0, credit: 0 }

  const isDraft = transaction?.status === 'draft'
  const isValidated = transaction?.status === 'validated'
  const isReversed = transaction?.status === 'reversed'

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="900px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {transaction && (
                  <>
                    <HStack gap={2} mb={1}>
                      <Badge variant="outline" size="sm">
                        {transaction.journal?.code}
                      </Badge>
                      {transaction.piece_number && (
                        <Badge variant="outline" size="sm" fontFamily="mono">
                          {transaction.piece_number}
                        </Badge>
                      )}
                      {getStatusBadge(transaction.status!)}
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {transaction.description}
                    </Heading>
                  </>
                )}
                {!transaction && <Heading size="md">Détail de la pièce</Heading>}
              </Box>

              {/* Actions */}
              <Box display="flex" gap={2} flexShrink={0}>
                {transaction && (isDraft || isValidated || isReversed) && (
                  <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
                    <MenuTrigger asChild>
                      <IconButton variant="ghost" size="sm" aria-label="Actions">
                        <MoreVertical size={18} />
                      </IconButton>
                    </MenuTrigger>
                    <MenuPositioner>
                      <MenuContent minW="200px" zIndex={9999}>
                          {/* Actions pour brouillon */}
                          {isDraft && (
                            <>
                              <MenuItem value="edit" onClick={() => onEdit?.(transaction)}>
                                <HStack gap={2}>
                                  <Edit size={16} />
                                  <Text>Modifier</Text>
                                </HStack>
                              </MenuItem>
                              <MenuItem value="validate" onClick={() => onValidate?.(transaction.id)}>
                                <HStack gap={2}>
                                  <Check size={16} />
                                  <Text>Valider</Text>
                                </HStack>
                              </MenuItem>
                              <Separator />
                            </>
                          )}

                          {/* Actions pour validé */}
                          {isValidated && (
                            <>
                              <MenuItem value="reverse" onClick={() => onReverse?.(transaction.id)}>
                                <HStack gap={2}>
                                  <RotateCcw size={16} />
                                  <Text>Extourner</Text>
                                </HStack>
                              </MenuItem>
                            </>
                          )}

                          {/* Dupliquer - toujours disponible */}
                          <MenuItem value="duplicate" onClick={() => onDuplicate?.(transaction)}>
                            <HStack gap={2}>
                              <Copy size={16} />
                              <Text>Dupliquer</Text>
                            </HStack>
                          </MenuItem>

                          {/* Supprimer - uniquement brouillon */}
                          {isDraft && (
                            <>
                              <Separator />
                              <MenuItem
                                value="delete"
                                onClick={() => onDelete?.(transaction.id)}
                                color="red.600"
                                _hover={{ bg: 'red.50' }}
                              >
                                <HStack gap={2}>
                                  <Trash2 size={16} />
                                  <Text>Supprimer</Text>
                                </HStack>
                              </MenuItem>
                            </>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                  </MenuRoot>
                )}
                <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                  <X size={18} />
                </IconButton>
              </Box>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Pièce comptable non trouvée" />}

            {transaction && (
              <VStack align="stretch" gap={0} flex={1} height="100%">
                {/* Informations principales */}
                <HStack
                  gap={4}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="blue.500">
                      <Calendar size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Date opération
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {formatDate(transaction.transaction_date)}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="purple.500">
                      <FileText size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Journal
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {transaction.journal?.name || transaction.journal?.code}
                      </Text>
                    </VStack>
                  </HStack>

                  {transaction.reference && (
                    <HStack gap={2} flex={1} minW="140px">
                      <Box color="orange.500">
                        <FileText size={16} />
                      </Box>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          Référence
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {transaction.reference}
                        </Text>
                      </VStack>
                    </HStack>
                  )}
                </HStack>

                {/* Écritures */}
                <Box px={6} pt={4} flex={1} overflow="auto">
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack justify="space-between">
                        <Heading size="sm">Écritures comptables</Heading>
                        <HStack gap={4}>
                          <Text fontSize="sm" color="gray.600">
                            {transaction.lines?.length || 0} ligne{(transaction.lines?.length || 0) > 1 ? 's' : ''}
                          </Text>
                          {totals.debit === totals.credit ? (
                            <Badge colorPalette="green" size="sm">Équilibré</Badge>
                          ) : (
                            <Badge colorPalette="red" size="sm">
                              Écart: {formatCurrency(Math.abs(totals.debit - totals.credit))}
                            </Badge>
                          )}
                        </HStack>
                      </HStack>
                    </Card.Header>
                    <Card.Body p={0}>
                      <Table.Root size="sm">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader w="80px">Compte</Table.ColumnHeader>
                            <Table.ColumnHeader>Libellé</Table.ColumnHeader>
                            <Table.ColumnHeader w="120px">Tiers</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="110px">Débit</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="110px">Crédit</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {transaction.lines?.map((line) => (
                            <Table.Row key={line.id}>
                              <Table.Cell fontFamily="mono" fontSize="sm" fontWeight="medium" color="blue.700">
                                {getAccountCode(line)}
                              </Table.Cell>
                              <Table.Cell>
                                <Text fontSize="sm">{line.label || transaction.description || '-'}</Text>
                              </Table.Cell>
                              <Table.Cell>
                                {line.owner && (
                                  <Text fontSize="xs" color="gray.600">
                                    {line.owner.first_name} {line.owner.last_name}
                                  </Text>
                                )}
                                {line.unit && (
                                  <Text fontSize="xs" color="gray.500">
                                    Lot {line.unit.unit_number}
                                  </Text>
                                )}
                              </Table.Cell>
                              <Table.Cell textAlign="right" fontFamily="mono" fontSize="sm">
                                {line.debit && line.debit > 0 ? formatCurrency(line.debit) : ''}
                              </Table.Cell>
                              <Table.Cell textAlign="right" fontFamily="mono" fontSize="sm">
                                {line.credit && line.credit > 0 ? formatCurrency(line.credit) : ''}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                          {/* Ligne de totaux */}
                          <Table.Row bg="gray.100" fontWeight="bold">
                            <Table.Cell colSpan={3} textAlign="right">
                              TOTAUX
                            </Table.Cell>
                            <Table.Cell textAlign="right" fontFamily="mono">
                              {formatCurrency(totals.debit)}
                            </Table.Cell>
                            <Table.Cell textAlign="right" fontFamily="mono">
                              {formatCurrency(totals.credit)}
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table.Root>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Historique */}
                <Box px={6} pt={4} pb={6}>
                  <Card.Root variant="outline">
                    <Card.Header py={3}>
                      <Heading size="sm">Historique</Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        {/* Création */}
                        <HStack gap={3}>
                          <Box color="gray.400">
                            <Clock size={16} />
                          </Box>
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium">Créée le</Text>
                            <Text fontSize="xs" color="gray.600">
                              {formatDateTime(transaction.created_at)}
                            </Text>
                          </VStack>
                        </HStack>

                        {/* Validation */}
                        {transaction.validated_at && (
                          <HStack gap={3}>
                            <Box color="green.500">
                              <Check size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">Validée le</Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDateTime(transaction.validated_at)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}

                        {/* Contrepassation - TODO: activer quand reversed_at sera ajouté au modèle */}
                        {/* {transaction.reversed_at && (
                          <HStack gap={3}>
                            <Box color="red.500">
                              <RotateCcw size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">Extournée le</Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDateTime(transaction.reversed_at)}
                              </Text>
                            </VStack>
                          </HStack>
                        )} */}

                        {/* Pièce liée (extourne) - TODO: activer quand reversal_of_id sera ajouté au modèle */}
                        {/* {transaction.reversal_of_id && (
                          <HStack gap={3}>
                            <Box color="orange.500">
                              <Link2 size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">Extourne de</Text>
                              <Text fontSize="xs" color="gray.600" fontFamily="mono">
                                {transaction.reversal_of_id}
                              </Text>
                            </VStack>
                          </HStack>
                        )} */}

                        {/* Dernière modification */}
                        {transaction.updated_at && transaction.updated_at !== transaction.created_at && (
                          <HStack gap={3}>
                            <Box color="blue.500">
                              <User size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">Modifiée le</Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDateTime(transaction.updated_at)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
